import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import LazyImage from "../../../../SharedComponents/ShowMedia/LazyImage";
import { useTranslation } from "react-i18next";

const PlaylistContent = ({ itIsPlaying, currentMediaPlaying, item, startTime, pause_start_time, showAll = false }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const { paused } = useSelector((state) => state.device);

  const [elapsedSeconds, setElapsedSeconds] = useState(() => {
    const now = paused ? moment(pause_start_time) : moment();
    return now.diff(moment(startTime), "seconds");
  });

  useEffect(() => {
    let interval;
    if (itIsPlaying) {
      interval = setInterval(() => {
        const now = moment();
        const start = moment(startTime);
        const diff = Math.max(0, now.diff(start, "seconds"));
        setElapsedSeconds(diff);
      }, 100);
    } 
    return () => clearInterval(interval);
  }, [itIsPlaying, startTime]);

  const getCurrentPlayingIndex = (playlist, elapsedSeconds) => {
    let total = 0;
    for (let i = 0; i < playlist.length; i++) {
        total += Number(playlist[i].custom_duration || 0);
        if (elapsedSeconds < total) return i;
    }
    return -1;
  };

  const currentIndex = itIsPlaying
    ? getCurrentPlayingIndex(item.playlist?.["MAIN"] || [], elapsedSeconds)
    : -1;

  useEffect(() => {
    if (itIsPlaying && currentIndex !== -1 && itemRefs.current[currentIndex] && containerRef.current) {
      itemRefs.current[currentIndex].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentIndex, itIsPlaying]);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col pt-3 pb-2 pl-1 relative ${showAll ? "" : "max-h-[300px] overflow-y-auto custom-scrollbar"}`}
    >
      {/* Visual Timeline Line */}
      <div className="absolute left-[19px] top-6 bottom-6 w-[2px] bg-slate-100 z-0"></div>

      {(item.playlist["MAIN"] || [])
        .reduce((acc, playlistItem) => {
            const prevDuration = acc.totalDuration;
            acc.items.push({ ...playlistItem, startOffset: prevDuration });
            acc.totalDuration += Number(playlistItem.custom_duration || 0);
            return acc;
        }, { items: [], totalDuration: 0 }).items
        .map((playlistItem, index) => {
          const isActive = index === currentIndex;
          let itemElapsed = 0;
          if (isActive) {
              itemElapsed = Math.floor(Math.max(0, Math.min(playlistItem.custom_duration, elapsedSeconds - playlistItem.startOffset)));
          }

          return (
            <div
              key={index}
              ref={el => itemRefs.current[index] = el}
              className={`relative flex items-center gap-4 p-2 rounded-2xl transition-all duration-500 group z-10 
                ${isActive ? "bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] ring-1 ring-slate-100 scale-[1.01] my-1" : "hover:bg-slate-50 opacity-70 hover:opacity-100"}`}
            >
              {/* Timeline Indicator */}
              <div 
                  className={`relative shrink-0 flex items-center justify-center w-6 h-6 rounded-full transition-all duration-500 z-10
                  ${isActive 
                      ? "bg-[var(--pp-btn-honey)] shadow-[0_0_15px_color-mix(in_srgb,var(--pp-btn-honey)_30%,transparent)] scale-110" 
                      : "bg-white border-2 border-slate-200 group-hover:border-slate-400"}`}
              >
                  {isActive ? (
                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping"></div>
                  ) : (
                      <div className="w-1 h-1 rounded-full bg-slate-300 group-hover:bg-slate-500"></div>
                  )}
              </div>

              {/* Media Preview Cell */}
              <div className="flex-1 min-w-0 flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden shrink-0 border transition-all
                      ${isActive ? 'border-[color-mix(in_srgb,var(--pp-btn-honey)_25%,white)] bg-white shadow-sm' : 'border-slate-100 bg-slate-50'}`}
                  >
                        {playlistItem.media_type ? (
                          ["HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(playlistItem.media_type) ? (
                            <span className="text-[8px] font-black text-slate-500 uppercase text-center leading-tight">{playlistItem.media_type.replace(/_/g, "\n")}</span>
                          ) : (
                            <LazyImage media={playlistItem} popupType="playlist_media_setting" index={playlistItem.id} styleClass="w-full h-full object-cover" />
                          )
                        ) : (
                           <i className={`text-sm text-slate-400 ${playlistItem.layout_name ? 'fa-solid fa-table-cells-large' : 'fa-solid fa-list-check'}`} />
                        )}
                  </div>

                  <div className="flex-1 min-w-0">
                      <div className={`text-[11px] font-bold truncate transition-colors ${isActive ? 'text-[var(--pp-btn-honey)]' : 'text-slate-600'}`}>
                          {playlistItem.media_name || playlistItem.playlist_name || playlistItem.layout_name || t("devices.preview_untitled_unit")}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[9px] font-black tracking-widest transition-colors ${isActive ? 'text-[var(--pp-btn-honey)]' : 'text-slate-400'}`}>
                           {isActive ? `${itemElapsed}s / ${playlistItem.custom_duration}s` : `${playlistItem.custom_duration}s`}
                        </span>
                        {playlistItem.media_type && (
                            <span className="text-[8px] font-bold px-1 rounded bg-slate-100 text-slate-400 uppercase tracking-tighter">
                                {playlistItem.media_type}
                            </span>
                        )}
                      </div>
                  </div>
              </div>
            </div>
          )
        })}
    </div>
  );
};

export default PlaylistContent;
